import {
  CALCULATOR_STANDARD_BUILDING_SHARE_PCT,
  PROPERTY_CALC_FIELDS,
  PROPERTY_CALC_KEYS,
  formatAmountInput,
  formatEuro,
  formatPercentInput,
  parseAmountInput,
  parsePercentInput,
  type CalculationAssumptions,
  type PropertyCalcDefaults,
  type PropertyCalcDefaultsBody,
  type PropertyCalcKey,
  type PropertySalesKey,
} from '@brimo/shared';
import { formatNumber, parseDecimal } from '../format';

/**
 * Draft of the „Objekt-Vorgaben" (0053, Nico Kremer's feedback 01.11.2026, D-361/D-362): the text of every field of
 * the sheet „Objekt-Vorgaben" and of the base-data group „Kalkulation", the `calcDefaults` PATCH body built from it,
 * the card's display values and the organization hint under each field. Shared by `CalcDefaultsCard` and
 * `BaseDataOnboarding` so both edit the same values the same way.
 */

export type CalcDefaultsKey = PropertyCalcKey | PropertySalesKey;
export type CalcDefaultsDraft = Record<CalcDefaultsKey, string>;

/** The sheet's groups in order (Kalkulation, Laufende Kosten je Einheit, Vertrieb; the storyline is separate). */
export const CALC_GROUPS: readonly { title: string; keys: readonly CalcDefaultsKey[] }[] = [
  { title: 'Kalkulation', keys: ['depreciationPct', 'buildingSharePct', 'interestPct', 'repaymentPct'] },
  {
    title: 'Laufende Kosten je Einheit',
    keys: ['sevCents', 'wegCents', 'reservePerSqmCents', 'otherCostsPerSqmCents'],
  },
  { title: 'Vertrieb', keys: ['priceFromCents', 'yieldFromPct', 'landAreaSqm'] },
];

/** The card's rows: the eight calculation values, then „Kaufpreise ab" and „Rendite ab". */
export const CARD_KEYS: readonly CalcDefaultsKey[] = [
  ...PROPERTY_CALC_KEYS,
  'priceFromCents',
  'yieldFromPct',
];

const EMPTY_VALUE = '–';
/** „4,7 %", „4,38 %", „80 %" — as many decimals as the value has (at most two are stored). */
const percentLabel = (value: number) => `${formatPercentInput(value)}\u00a0%`;

export function calcDefaultsDraftOf(values: PropertyCalcDefaults): CalcDefaultsDraft {
  const draft = {} as CalcDefaultsDraft;
  for (const spec of Object.values(PROPERTY_CALC_FIELDS)) {
    const value = values[spec.key];
    if (value === null) draft[spec.key] = '';
    else if (typeof value === 'string') draft[spec.key] = value;
    else if (spec.kind === 'amount') draft[spec.key] = formatAmountInput(value);
    else draft[spec.key] = formatNumber(value, 2);
  }
  return draft;
}

/**
 * The `calcDefaults` body for the given keys and the field errors of unreadable numbers (keyed like the API's
 * validation errors, `calcDefaults.<key>`). Empty text clears the value (null).
 */
export function calcDefaultsBody(
  draft: CalcDefaultsDraft,
  keys: readonly CalcDefaultsKey[],
): { body: PropertyCalcDefaultsBody; errors: Record<string, string> } {
  const body: Record<string, number | string | null> = {};
  const errors: Record<string, string> = {};
  for (const key of keys) {
    const spec = PROPERTY_CALC_FIELDS[key];
    const text = draft[key].trim();
    if (spec.kind === 'text') {
      body[key] = text === '' ? null : text;
      continue;
    }
    if (text === '') {
      body[key] = null;
      continue;
    }
    const parsed =
      spec.kind === 'amount'
        ? parseAmountInput(text)
        : spec.kind === 'percent'
          ? parsePercentInput(text)
          : parseDecimal(text);
    if (parsed === null) errors[`calcDefaults.${key}`] = 'Bitte eine Zahl angeben.';
    else body[key] = parsed;
  }
  return { body, errors };
}

/** The value as the card shows it: „4,38 %", „35,00 €/Mon.", „0,50 €/m² mtl.", „189.000 €", „–" when empty. */
export function calcValueLabel(key: CalcDefaultsKey, value: number | string | null): string {
  if (value === null || value === '') return EMPTY_VALUE;
  if (typeof value === 'string') return value;
  const spec = PROPERTY_CALC_FIELDS[key];
  if (key === 'priceFromCents') return formatEuro(value);
  if (spec.kind === 'percent') return percentLabel(value);
  if (spec.kind === 'amount') return `${formatAmountInput(value)} ${spec.suffix}`;
  return `${formatNumber(value, 2)} ${spec.suffix}`;
}

/**
 * The muted hint under a calculation field: the organization's value („Organisation: 4,7 %"), for the fields the
 * organization has no setting for the calculator's fixed value („Standardwert: 80 %" / „Standardwert: 0,00
 * €/Mon."). Without read access to the organization's assumptions (`org` null) only the fixed values are named.
 */
export function organizationHint(
  key: CalcDefaultsKey,
  org: Pick<
    CalculationAssumptions,
    | 'calcDepreciationPct'
    | 'calcInterestPct'
    | 'calcRepaymentPct'
    | 'calcReservePerSqmCents'
    | 'calcOtherCostsPerSqmCents'
  > | null,
): string | undefined {
  switch (key) {
    case 'buildingSharePct':
      return `Standardwert: ${percentLabel(CALCULATOR_STANDARD_BUILDING_SHARE_PCT)}`;
    case 'sevCents':
    case 'wegCents':
      return `Standardwert: ${calcValueLabel(key, 0)}`;
    case 'depreciationPct':
      return org ? `Organisation: ${percentLabel(org.calcDepreciationPct)}` : undefined;
    case 'interestPct':
      return org ? `Organisation: ${percentLabel(org.calcInterestPct)}` : undefined;
    case 'repaymentPct':
      return org ? `Organisation: ${percentLabel(org.calcRepaymentPct)}` : undefined;
    case 'reservePerSqmCents':
      return org ? `Organisation: ${calcValueLabel(key, org.calcReservePerSqmCents)}` : undefined;
    case 'otherCostsPerSqmCents':
      return org ? `Organisation: ${calcValueLabel(key, org.calcOtherCostsPerSqmCents)}` : undefined;
    case 'priceFromCents':
    case 'yieldFromPct':
    case 'landAreaSqm':
    case 'storyline':
      return undefined;
  }
}
